/**
 * Client-side port of the backend's `_aggregate_matrix` (collector.py), so the
 * priority×state matrix can be recomputed when the work-item-type filter changes.
 * Kept deliberately faithful: with all types selected it must match the backend's
 * `priority_state_matrix` / `priority_state_matrix_bugs` exactly.
 */
import type {
  ByStateCounts,
  PriorityRowCounts,
  StateKey,
  WorkItem,
} from "@/types/sprint-report"

const PRIORITY_COLORS: Record<string, string> = {
  P0: "#C00000",
  P1: "#E97132",
  P2: "#BF8F00",
  P3: "#595959",
}

const STATE_KEYS: StateKey[] = [
  "new",
  "grooming",
  "ux_ready",
  "ready",
  "dev_ready",
  "committed",
  "dev_wip",
  "dev_pr",
  "dev_done",
  "qa_ready",
  "qa_wip",
  "done",
]

const STATE_COLUMN_MAP: Record<string, StateKey> = {
  new: "new",
  grooming: "grooming",
  "ux ready": "ux_ready",
  ready: "ready",
  "dev ready": "dev_ready",
  committed: "committed",
  "dev wip": "dev_wip",
  "dev pr": "dev_pr",
  "dev done": "dev_done",
  "qa ready": "qa_ready",
  "qa wip": "qa_wip",
  done: "done",
  closed: "done",
}

function stateToColumn(state: string): StateKey | "other" {
  return STATE_COLUMN_MAP[state.trim().toLowerCase()] ?? "other"
}

function emptyByState(): ByStateCounts {
  return {
    new: 0,
    grooming: 0,
    ux_ready: 0,
    ready: 0,
    dev_ready: 0,
    committed: 0,
    dev_wip: 0,
    dev_pr: 0,
    dev_done: 0,
    qa_ready: 0,
    qa_wip: 0,
    done: 0,
  }
}

function emptyRow(color: string, isSubtotal = false): PriorityRowCounts {
  return {
    is_subtotal: isSubtotal,
    color,
    done_in_previous_sprint: 0,
    backlog_plus_future: 0,
    current_sprint_size: 0,
    by_state: emptyByState(),
    remaining_in_sprint: 0,
    new_addition: 0,
    remaining_overall: 0,
  }
}

export function aggregateMatrix(items: WorkItem[]): Record<string, PriorityRowCounts> {
  const matrix: Record<string, PriorityRowCounts> = {}
  for (const key of ["P0", "P1", "P1-A", "P1-B", "P2", "P2-A", "P2-B", "P3"]) {
    const major = key.split("-")[0]
    matrix[key] = emptyRow(PRIORITY_COLORS[major])
  }

  for (const item of items) {
    const sprint = item.sprint
    const { major, minor } = item.priority
    const key = minor ? `${major}-${minor}` : major
    if (!matrix[key]) matrix[key] = emptyRow(PRIORITY_COLORS[major] ?? "#000000")
    const row = matrix[key]
    const isDone = item.state_category === "done"

    // Backlog + future (open items only — matches the Done-exclusion fix).
    if (!sprint || sprint.is_future || sprint.is_past) {
      if (!isDone) row.backlog_plus_future += 1
      continue
    }
    if (sprint.is_previous) {
      if (isDone) row.done_in_previous_sprint += 1
      continue
    }
    if (sprint.is_current) {
      row.current_sprint_size += 1
      const col = stateToColumn(item.state)
      if (col !== "other") row.by_state[col] += 1
      if (!isDone) row.remaining_in_sprint += 1
    }
    if (sprint.is_next && !isDone) row.backlog_plus_future += 1
  }

  for (const major of ["P1", "P2"]) {
    const sub = emptyRow(PRIORITY_COLORS[major], true)
    for (const key of [major, `${major}-A`, `${major}-B`]) {
      const r = matrix[key]
      if (!r) continue
      sub.done_in_previous_sprint += r.done_in_previous_sprint
      sub.backlog_plus_future += r.backlog_plus_future
      sub.current_sprint_size += r.current_sprint_size
      sub.remaining_in_sprint += r.remaining_in_sprint
      for (const s of STATE_KEYS) sub.by_state[s] += r.by_state[s]
    }
    sub.remaining_overall = sub.remaining_in_sprint + sub.backlog_plus_future
    matrix[`${major}__subtotal`] = sub
  }

  for (const [key, row] of Object.entries(matrix)) {
    if (!key.endsWith("__subtotal")) {
      row.remaining_overall = row.remaining_in_sprint + row.backlog_plus_future
    }
  }
  return matrix
}
